import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Chip } from 'primereact/chip';
import { Divider } from 'primereact/divider';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import { ShippingLineItem } from '../../../DeShippingTableWrapper';

interface DeclineReasonDialogProps {
  visible: boolean;
  onHide: () => void;
  onConfirm: (reason: string) => void;
  loading?: boolean;
  // For single item decline
  selectedLineItem?: ShippingLineItem | null;
  // For bulk decline
  totalItems?: number;
  // Decline type
  declineType: 'single' | 'all';
}

const PREDEFINED_REASONS = [
  'Out of stock',
  'Damaged product',
  'Incorrect item',
  'Quality issues',
  'Shipping restrictions',
  'Customer request',
  'Inventory discrepancy'
];

export const DeclineReasonDialog: React.FC<DeclineReasonDialogProps> = ({
  visible,
  onHide,
  onConfirm,
  loading = false,
  selectedLineItem,
  totalItems,
  declineType,
}) => {
  const [declineReason, setDeclineReason] = useState('');
  const isBulk = declineType === 'all';

  // Reset state when dialog closes
  useEffect(() => {
    if (!visible) {
      setDeclineReason('');
    }
  }, [visible]);

  const handleConfirm = () => {
    const finalReason = declineReason.trim();
    if (finalReason) {
      onConfirm(finalReason);
    }
  };

  const handleCancel = () => {
    setDeclineReason('');
    onHide();
  };

  const handlePredefinedReasonSelect = (reason: string) => {
    setDeclineReason(reason);
  };

  const handleCustomReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDeclineReason(e.target.value);
  };

  const isReasonProvided = declineReason.trim();

  const renderBulkWarning = () => {
    if (!isBulk) return null;

    return (
      <Message
        severity="error"
        text={`You are about to decline ${totalItems} items. This action cannot be undone.`}
        className="mb-4"
        icon="pi pi-exclamation-triangle"
      />
    );
  };

  const dialogHeader = (
    <div className="flex align-items-center gap-3">
      <i className={`pi ${isBulk ? 'pi-exclamation-triangle' : 'pi-times-circle'} text-red-500 text-xl`}></i>
      <span className="text-xl font-bold">
        {isBulk ? 'Decline All Items' : 'Decline Item'}
      </span>
    </div>
  );

  const footerContent = (
    <div className="flex justify-content-between align-items-center">
      <div></div>
      <div className="flex gap-3">
        <Button
          label="Cancel"
          icon="pi pi-times"
          className="p-button-text p-button-lg"
          onClick={handleCancel}
          disabled={loading}
        />
        <Button
          label={isBulk ? 'Decline All Items' : 'Decline Item'}
          icon="pi pi-check"
          className="p-button-danger p-button-lg"
          onClick={handleConfirm}
          disabled={!isReasonProvided || loading}
          loading={loading}
        />
      </div>
    </div>
  );

  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      style={{ width: '600px' }}
      modal
      onHide={handleCancel}
      className="decline-reason-dialog"
      footer={footerContent}
    >
      <div className="flex flex-column gap-4">
        {renderBulkWarning()}

        <Card 
          className="shadow-2"
          title={
            <div className="flex align-items-center gap-2">
              <i className="pi pi-comment text-primary"></i>
              <span>Decline Reason</span>
              <span className="text-red-500">*</span>
            </div>
          }
          subTitle="Select a quick reason or provide your own custom reason"
          style={{ padding: '0' }}
          pt={{
            body: { className: 'p-4' },
            content: { className: 'p-0' }
          }}
        >
          <div className="mb-4">
            <div className="text-700 font-semibold mb-3">Quick Select:</div>
            <div className="flex flex-wrap gap-2">
              {PREDEFINED_REASONS.map((reason) => (
                <Button
                  key={reason}
                  label={reason}
                  className="p-button-outlined p-button-sm"
                  onClick={() => handlePredefinedReasonSelect(reason)}
                />
              ))}
            </div>
          </div>

          <Divider>
            <span className="text-600 font-semibold">OR</span>
          </Divider>

          <div className="mt-4">
            <div className="text-700 font-semibold mb-3">Custom Reason:</div>
            <InputTextarea
              value={declineReason}
              onChange={handleCustomReasonChange}
              placeholder="Enter a custom decline reason..."
              rows={5}
              className="w-full"
              disabled={loading}
              style={{ 
                minHeight: '120px',
                width: '100%',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </Card>

        {!isReasonProvided && (
          <Message
            severity="warn"
            text="Please select a predefined reason or provide a custom reason to continue."
            className="mt-2"
          />
        )}
      </div>
    </Dialog>
  );
};